import Container from "@cloudscape-design/components/container";
import ColumnLayout from "@cloudscape-design/components/column-layout";
import Box from "@cloudscape-design/components/box";
import Header from "@cloudscape-design/components/header";
import Table from "@cloudscape-design/components/table";
import StatusIndicator from "@cloudscape-design/components/status-indicator";
import SpaceBetween from "@cloudscape-design/components/space-between";

export interface EcsTask {
  taskId: string;
  lastStatus: string;
  healthStatus?: string;
  privateIp?: string;
  startedAt?: string;
  containers?: { name: string }[];
}

export interface EcsDeployment {
  id: string;
  status: string;
  desiredCount: number;
  runningCount: number;
  pendingCount: number;
  taskDefinition: string;
  createdAt?: string;
  rolloutState?: string;
}

export interface EcsServiceData {
  serviceName: string;
  clusterName: string;
  status: string;
  desiredCount: number;
  runningCount: number;
  pendingCount?: number;
  taskDefinition: string;
  launchType: string;
  createdAt?: string;
  tasks?: EcsTask[];
  deployments?: EcsDeployment[];
  tags?: { key: string; value: string }[];
}

function taskStatusType(s: string) {
  const map: Record<string, "success" | "stopped" | "warning" | "info"> = {
    RUNNING: "success",
    STOPPED: "stopped",
    PENDING: "warning",
  };
  return map[s] || "info";
}

export default function EcsServiceDetail({ service }: { service: EcsServiceData }) {
  return (
    <SpaceBetween size="l">
      <Container header={<Header variant="h2">Service Details</Header>}>
        <ColumnLayout columns={2} variant="text-grid">
          <div><Box variant="awsui-key-label">Service Name</Box><div>{service.serviceName}</div></div>
          <div><Box variant="awsui-key-label">Cluster</Box><div>{service.clusterName}</div></div>
          <div><Box variant="awsui-key-label">Status</Box><StatusIndicator type={service.status === "ACTIVE" ? "success" : "info"}>{service.status}</StatusIndicator></div>
          <div><Box variant="awsui-key-label">Desired Count</Box><div>{service.desiredCount}</div></div>
          <div><Box variant="awsui-key-label">Running Count</Box><div>{service.runningCount}</div></div>
          <div><Box variant="awsui-key-label">Pending Count</Box><div>{service.pendingCount ?? "—"}</div></div>
          <div><Box variant="awsui-key-label">Task Definition</Box><div>{service.taskDefinition}</div></div>
          <div><Box variant="awsui-key-label">Launch Type</Box><div>{service.launchType}</div></div>
          <div><Box variant="awsui-key-label">Created At</Box><div>{service.createdAt ?? "—"}</div></div>
        </ColumnLayout>
      </Container>
      <Table
        header={<Header variant="h2">Tasks</Header>}
        columnDefinitions={[
          { id: "taskId", header: "Task ID", cell: (item: EcsTask) => item.taskId },
          { id: "lastStatus", header: "Status", cell: (item: EcsTask) => <StatusIndicator type={taskStatusType(item.lastStatus)}>{item.lastStatus}</StatusIndicator> },
          { id: "healthStatus", header: "Health", cell: (item: EcsTask) => item.healthStatus || "—" },
          { id: "privateIp", header: "Private IP", cell: (item: EcsTask) => item.privateIp || "—" },
          { id: "startedAt", header: "Started At", cell: (item: EcsTask) => item.startedAt || "—" },
          { id: "containers", header: "Containers", cell: (item: EcsTask) => (item.containers || []).map((c) => c.name).join(", ") },
        ]}
        items={service.tasks || []}
        empty="No tasks"
      />
      <Table
        header={<Header variant="h2">Deployments</Header>}
        columnDefinitions={[
          { id: "id", header: "ID", cell: (item: EcsDeployment) => item.id },
          { id: "status", header: "Status", cell: (item: EcsDeployment) => item.status },
          { id: "taskDefinition", header: "Task Definition", cell: (item: EcsDeployment) => item.taskDefinition },
          { id: "desiredCount", header: "Desired", cell: (item: EcsDeployment) => item.desiredCount },
          { id: "runningCount", header: "Running", cell: (item: EcsDeployment) => item.runningCount },
          { id: "pendingCount", header: "Pending", cell: (item: EcsDeployment) => item.pendingCount },
          { id: "rolloutState", header: "Rollout State", cell: (item: EcsDeployment) => item.rolloutState || "—" },
          { id: "createdAt", header: "Created At", cell: (item: EcsDeployment) => item.createdAt || "—" },
        ]}
        items={service.deployments || []}
        empty="No deployments"
      />
      <Table
        header={<Header variant="h2">Tags</Header>}
        columnDefinitions={[
          { id: "key", header: "Key", cell: (item: { key: string; value: string }) => item.key },
          { id: "value", header: "Value", cell: (item: { key: string; value: string }) => item.value },
        ]}
        items={service.tags || []}
        empty="No tags"
      />
    </SpaceBetween>
  );
}
